/**
 * Direct (secret) message thread — /(app)/games/[id]/dm/[userId]
 *
 * A private 1:1 conversation with one other player in the game. Reached by
 * tapping "Message" on a player in the roster panel (which passes the partner's
 * display name via the `name` query param so the header reads correctly even
 * before any messages exist).
 *
 * ⚠️ KEEP THIS A ROUTE (not a Modal) — see MessageThread for the
 * keyboard-handling rationale.
 */
import React, { useCallback, useMemo } from 'react'
import { Redirect, useLocalSearchParams, useRouter } from 'expo-router'
import { useAuth } from '../../../../../hooks/useAuth'
import { useGame } from '../../../../../hooks/useGames'
import { dmRedirectForTeamGame } from '../../../../../lib/teamChat'
import {
  useDmThread,
  usePostDm,
  useMarkDmRead,
  useToggleDmReaction,
} from '../../../../../hooks/useDirectMessages'
import { MessageThread, type ThreadMessage } from '../../../../../components/game/MessageThread'
import { useToast } from '../../../../../components/ui/Toast'
import type { ChatReactionEmoji } from '@warcarp/api-types'

export default function DirectMessageScreen() {
  const { id } = useLocalSearchParams<{ id: string }>()
  const { user } = useAuth()
  // Team games have no 1:1 secret messages: an old link or push lands on the
  // team chat (or the game, for a player without one) instead.
  const { data: game } = useGame(id)
  const redirect = dmRedirectForTeamGame(game, user?.id)
  if (redirect) return <Redirect href={redirect as never} />
  return <DirectMessageThread />
}

function DirectMessageThread() {
  const { id, userId, name, focus } = useLocalSearchParams<{ id: string; userId: string; name?: string; focus?: string }>()
  const gameId = id ?? ''
  const otherUserId = userId ?? ''
  const { user } = useAuth()
  const { showToast } = useToast()
  const router = useRouter()

  const { data } = useDmThread(gameId, otherUserId)
  const postDm = usePostDm(gameId, otherUserId)
  const markRead = useMarkDmRead(gameId, otherUserId)
  const toggleReaction = useToggleDmReaction(gameId, otherUserId)

  const rawMessages = useMemo(() => data?.messages ?? [], [data])

  const messages = useMemo<ThreadMessage[]>(
    () =>
      rawMessages.map((m) => ({
        id: m.id,
        authorId: m.fromUserId,
        authorName: m.fromDisplayName,
        content: m.content,
        createdAt: m.createdAt,
        reactions: m.reactions ?? [],
      })),
    [rawMessages],
  )

  // Header title: prefer the partner's name from a message they sent, else the
  // name passed in the route param, else a neutral fallback.
  const partnerName =
    rawMessages.find((m) => m.fromUserId === otherUserId)?.fromDisplayName ??
    (typeof name === 'string' ? name : undefined) ??
    'Player'

  // First still-unread inbound message → drives the "NEW" divider. We do NOT
  // mark read on open: the divider (and unread badge) stay until the viewer
  // focuses the composer or leaves the thread, at which point MessageThread
  // fires onSeen → markRead. The fetched rows keep readAt=null until then.
  const firstUnreadIndex = rawMessages.findIndex(
    (m) => m.toUserId === user?.id && m.readAt === null,
  )

  const handleSeen = useCallback(() => {
    markRead.mutate()
  }, [markRead])

  const close = useCallback(() => {
    if (router.canGoBack()) router.back()
    else router.replace(`/(app)/games/${gameId}`)
  }, [router, gameId])

  const handleSend = useCallback(
    async (content: string) => {
      await postDm.mutateAsync({ content })
    },
    [postDm],
  )

  const handleReact = useCallback(
    (messageId: string, emoji: ChatReactionEmoji) => {
      toggleReaction.mutate(
        { messageId, emoji },
        { onError: () => showToast("Couldn't update reaction — please try again.", 'error') },
      )
    },
    [toggleReaction, showToast],
  )

  return (
    <MessageThread
      title={`🔒 ${partnerName}`}
      subtitle={`Only you and ${partnerName} can see these`}
      messages={messages}
      currentUserId={user?.id}
      onSend={handleSend}
      isSending={postDm.isPending}
      composerDisabled={!data || data.messagingAvailable === false}
      onClose={close}
      firstUnreadIndex={firstUnreadIndex}
      onSeen={handleSeen}
      onReact={data?.messagingAvailable === false ? undefined : handleReact}
      focusMessageId={focus || undefined}
      onReport={(message) => router.push({ pathname: '/(app)/settings/blocked-users', params: { block: message.authorId, messageId: message.id, kind: 'dm' } })}
      reactingMessageId={toggleReaction.isPending ? toggleReaction.variables?.messageId : undefined}
      emptyIcon="🔒"
      emptyText="No messages yet. Say something in secret."
      placeholder={!data ? 'Loading conversation…' : data.messagingAvailable === false ? `Messaging unavailable with ${data.otherUsername ?? partnerName}` : `Message ${partnerName}…`}
      domIdPrefix="dm"
    />
  )
}
